import React, { useMemo, useState, useEffect } from 'react';
import type { StaffMember, Shift, Schedule, AvailabilityStatus, DayOfWeek, Notification, TimeOffRequest, TimeOffRequestStatus, TimeOffRequestType, Department, ScheduleEntry, ShuttleReservation, PickupLocation, ParkingReservation } from '../../types';
import { DAY_INDEX_MAP } from '../../types';
import { getWeekStartDate, getWeekDates, formatDateYYYYMMDD, formatDayAndDate, formatDateRange, getDayOfWeekFromDate } from '../../utils/dateUtils';
import AvailabilityCalendar from '../staff/AvailabilityCalendar';
import Accordion from '../ui/Accordion';
import { Icons } from '../ui/Icons';
import { isEqual } from 'lodash-es';
import NotificationBell from '../notifications/NotificationBell';
import NotificationItem from '../notifications/NotificationItem';
import { toast } from 'react-hot-toast';
import ReplacementSelectionModal from './ReplacementSelectionModal';
import DateRangeSelectionModal from './DateRangeSelectionModal';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';
import DailyShiftDetailsModal from './DailyShiftDetailsModal';
import HelpCenterView from '../help/HelpCenterView';

interface StaffPortalProps {
    staffMember: StaffMember;
    shifts: Shift[];
    schedule: Schedule;
    departments: Department[];
    allStaff: StaffMember[];
    onSaveStaff: (staff: StaffMember) => void;
    notifications: Notification[];
    onMarkNotificationAsRead: (id: string) => void;
    onMarkAllNotificationsAsRead: () => void;
    onClearNotifications: () => void;
    timeOffRequests: TimeOffRequest[];
    onCreateTimeOffRequest: (request: Omit<TimeOffRequest, 'id' | 'status' | 'createdAt' | 'staffName'>) => void;
    onDeleteTimeOffRequest: (requestId: string) => void;
    onReplacementDecision: (requestId: string, accepted: boolean) => void;
    shuttleReservations: ShuttleReservation[];
    pickupLocations: PickupLocation[];
    onLogout: () => void;
    onSaveShuttleReservation: (reservation: Omit<ShuttleReservation, 'id'> & { id?: string }) => void;
    onDeleteShuttleReservation: (id: string) => void;
    onInitiateShuttlePrint?: (date: string) => void;
    propertyName: string;
    weekStartDay: DayOfWeek;
}

const StaffPortal: React.FC<StaffPortalProps> = ({ 
    staffMember, 
    shifts, 
    schedule, 
    departments, 
    allStaff, 
    onSaveStaff, 
    notifications, 
    onMarkNotificationAsRead, 
    onMarkAllNotificationsAsRead, 
    onClearNotifications,
    timeOffRequests, 
    onCreateTimeOffRequest, 
    onDeleteTimeOffRequest, 
    onReplacementDecision,
    shuttleReservations,
    pickupLocations,
    onSaveShuttleReservation,
    onDeleteShuttleReservation,
    onLogout,
    onInitiateShuttlePrint,
    propertyName,
    weekStartDay
}) => {
    const [activeTab, setActiveTab] = useState<'schedule' | 'availability' | 'profile' | 'timeOff' | 'shuttle' | 'help'>('schedule');
    const [portalView, setPortalView] = useState<'main' | 'notifications'>('main');
    const [editableStaff, setEditableStaff] = useState<StaffMember>(staffMember);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [selectedDateForDetails, setSelectedDateForDetails] = useState<string | null>(null);
    const [shuttleDate, setShuttleDate] = useState(formatDateYYYYMMDD(new Date()));
    const [isBookingShuttle, setIsBookingShuttle] = useState(false);
    const [bookingForm, setBookingForm] = useState<Omit<ShuttleReservation, 'id' | 'createdAt' | 'date'>>({
        locationId: '',
        direction: 'FROM_HOTEL',
        time: '09:00',
        guests: 1,
        roomNumber: '',
        status: 'pending'
    });

    // Time Off Request Form State
    const [timeOffStartDate, setTimeOffStartDate] = useState('');
    const [timeOffEndDate, setTimeOffEndDate] = useState('');
    const [timeOffReason, setTimeOffReason] = useState('');
    const [timeOffType, setTimeOffType] = useState<TimeOffRequestType>('DIRECT');
    const [replacementStaffId, setReplacementStaffId] = useState<string | null>(null);
    const [isReplacementModalOpen, setIsReplacementModalOpen] = useState(false);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);

    const timeOffDeleteHandler = useDeleteHandler<TimeOffRequest>({
        onDeleteAsync: async (req) => onDeleteTimeOffRequest(req.id),
        entityName: 'Time Off Request',
        getDisplayName: (req) => `for ${req.startDate}`,
        getItemId: (req) => req.id,
    });


    const shuttleDeleteHandler = useDeleteHandler<ShuttleReservation>({
        onDeleteAsync: async (res) => onDeleteShuttleReservation(res.id),
        entityName: 'Shuttle Reservation',
        getDisplayName: (res) => `Room ${res.roomNumber} at ${res.time}`,
        getItemId: (res) => res.id,
    });

    useEffect(() => {
        setEditableStaff(staffMember);
    }, [staffMember]);

    const personalSchedule = schedule[staffMember.id] || {};
    const shiftsMap = useMemo(() => new Map<string, Shift>(shifts.map(s => [s.id, s])), [shifts]);
    
    const weekStartDayIndex = useMemo(() => DAY_INDEX_MAP[weekStartDay], [weekStartDay]);
    
    const timeSlots = useMemo(() => {
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let min = 0; min < 60; min += 30) {
                const h = hour.toString().padStart(2, '0');
                const m = min.toString().padStart(2, '0');
                slots.push(`${h}:${m}`);
            }
        }
        return slots;
    }, []);

    const thisWeekStartDate = useMemo(() => getWeekStartDate(new Date(), weekStartDayIndex), [weekStartDayIndex]);
    const nextWeekStartDate = useMemo(() => {
        const date = new Date(thisWeekStartDate);
        date.setDate(date.getDate() + 7);
        return date;
    }, [thisWeekStartDate]);

    const thisWeekDates = useMemo(() => getWeekDates(thisWeekStartDate), [thisWeekStartDate]);
    const nextWeekDates = useMemo(() => getWeekDates(nextWeekStartDate), [nextWeekStartDate]);
    
    const hasChanges = !isEqual(staffMember, editableStaff);
    const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

    const todayString = useMemo(() => formatDateYYYYMMDD(new Date()), []);
    const nextShiftDateString = useMemo(() => {
        const allDates = [...thisWeekDates, ...nextWeekDates];
        const upcomingAssignedDates = allDates
            .map(date => formatDateYYYYMMDD(date))
            .filter(dateString => 
                dateString >= todayString && 
                personalSchedule[dateString]?.status === 'ASSIGNED'
            )
            .sort();
        
        return upcomingAssignedDates.length > 0 ? upcomingAssignedDates[0] : null;
    }, [thisWeekDates, nextWeekDates, personalSchedule, todayString]);
    
    const calculateWeeklyHours = (dates: Date[], schedule: { [date: string]: ScheduleEntry }, shifts: Map<string, Shift>): number => {
        let totalHours = 0;
        for (const date of dates) {
            const dateString = formatDateYYYYMMDD(date);
            const entry = schedule[dateString];
            if (entry?.status === 'ASSIGNED' && entry.shiftId) {
                const shift = shifts.get(entry.shiftId);
                if (shift) {
                    const start = new Date(`1970-01-01T${shift.startTime}:00`);
                    const end = new Date(`1970-01-01T${shift.endTime}:00`);
                    if (end < start) {
                        end.setDate(end.getDate() + 1);
                    }
                    totalHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                }
            }
        }
        return totalHours;
    };

    const thisWeekHours = useMemo(() => calculateWeeklyHours(thisWeekDates, personalSchedule, shiftsMap), [thisWeekDates, personalSchedule, shiftsMap]);
    const nextWeekHours = useMemo(() => calculateWeeklyHours(nextWeekDates, personalSchedule, shiftsMap), [nextWeekDates, personalSchedule, shiftsMap]);

    const dailyDepartmentHours = useMemo(() => {
        const hours: { [date: string]: { [depId: string]: number } } = {};
        
        [...thisWeekDates, ...nextWeekDates].forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            hours[dateString] = {};
            departments.forEach(dep => {
                hours[dateString][dep.id] = 0;
            });
        });

        allStaff.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule && person.departmentId) {
                const depId = person.departmentId;
                [...thisWeekDates, ...nextWeekDates].forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED' && entry.shiftId) {
                        const shift = shiftsMap.get(entry.shiftId);
                        if (shift) {
                            const start = new Date(`1970-01-01T${shift.startTime}:00`);
                            const end = new Date(`1970-01-01T${shift.endTime}:00`);
                            if (end < start) {
                                end.setDate(end.getDate() + 1);
                            }
                            const shiftHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                            if (hours[dateString] && typeof hours[dateString][depId] === 'number') {
                                hours[dateString][depId] += shiftHours;
                            }
                        }
                    }
                });
            }
        });

        return hours;
    }, [schedule, allStaff, departments, thisWeekDates, nextWeekDates, shiftsMap]);

    const departmentWeeklyHours = useMemo(() => {
        const weekly: { [week: string]: { [depId: string]: number } } = {
            this: {},
            next: {}
        };

        departments.forEach(dep => {
            weekly.this[dep.id] = 0;
            weekly.next[dep.id] = 0;
        });

        thisWeekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            departments.forEach(dep => {
                weekly.this[dep.id] += dailyDepartmentHours[dateString]?.[dep.id] || 0;
            });
        });

        nextWeekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            departments.forEach(dep => {
                weekly.next[dep.id] += dailyDepartmentHours[dateString]?.[dep.id] || 0;
            });
        });

        return weekly;
    }, [departments, thisWeekDates, nextWeekDates, dailyDepartmentHours]);

    const getInitials = (name: string) => {
        const names = name.split(' ');
        if (names.length > 1 && names[names.length - 1]) {
            return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        setEditableStaff(prev => ({
            ...prev, 
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value 
        }));
    };

    const handleOverridesChange = (overrides: { [date: string]: AvailabilityStatus; }) => {
        setEditableStaff(prev => ({ ...prev, availabilityOverrides: overrides }));
    };
    
    const handleDefaultAvailabilityChange = (day: DayOfWeek, status: AvailabilityStatus) => {
        setEditableStaff(prev => ({
            ...prev,
            availability: { ...prev.availability, [day]: status }
        }));
    };

    const handleSaveChanges = () => {
        onSaveStaff(editableStaff);
    };

    const handleTimeOffSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // --- START VALIDATION ---
        if (!timeOffStartDate || !timeOffEndDate) {
            toast.error("Please select a start and end date.");
            return;
        }
        if (new Date(timeOffEndDate) < new Date(timeOffStartDate)) {
            toast.error("End date cannot be before the start date.");
            return;
        }

        if (timeOffType === 'REPLACED') {
            if (!replacementStaffId) {
                toast.error("Please select a replacement staff member for a 'Replaced' request.");
                return;
            }
            if (replacementStaffId === staffMember.id) {
                toast.error("You cannot select yourself as a replacement.");
                return;
            }

            const replacementStaff = allStaff.find(s => s.id === replacementStaffId);
            if (!replacementStaff) {
                toast.error("Could not find the selected replacement staff member.");
                return;
            }

            const getResolvedAvailability = (person: StaffMember, date: Date): AvailabilityStatus => {
                const dateString = formatDateYYYYMMDD(date);
                if (person.availabilityOverrides && person.availabilityOverrides[dateString]) {
                    return person.availabilityOverrides[dateString];
                }
                const dayOfWeek = getDayOfWeekFromDate(date);
                return (person.availability && person.availability[dayOfWeek]) || 'Available';
            };

            const parseDateAsLocal = (dateString: string) => {
                const [year, month, day] = dateString.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            let currentDate = parseDateAsLocal(timeOffStartDate);
            const endDate = parseDateAsLocal(timeOffEndDate);

            while (currentDate <= endDate) {
                const dateString = formatDateYYYYMMDD(currentDate);

                const availability = getResolvedAvailability(replacementStaff, currentDate);
                if (availability === 'Unavailable') {
                    toast.error(`${replacementStaff.name} is unavailable on ${dateString}.`);
                    return;
                }

                const replacementSchedule = schedule[replacementStaff.id];
                const entry = replacementSchedule?.[dateString];
                if (entry && entry.status !== 'OFF' && entry.status !== 'UNASSIGNED') {
                    toast.error(`${replacementStaff.name} already has an assignment on ${dateString}.`);
                    return;
                }

                currentDate.setDate(currentDate.getDate() + 1);
            }
        }
        // --- END VALIDATION ---

        onCreateTimeOffRequest({
            employeeId: staffMember.id,
            startDate: timeOffStartDate,
            endDate: timeOffEndDate,
            reason: timeOffReason,
            type: timeOffType,
            replacementEmployeeId: timeOffType === 'REPLACED' ? replacementStaffId : null,
        });

        // Reset form state
        setTimeOffStartDate('');
        setTimeOffEndDate('');
        setTimeOffReason('');
        setTimeOffType('DIRECT');
        setReplacementStaffId(null);
        toast.success("Your time off request has been submitted.");
    };

    const renderScheduleList = (dates: Date[]) => (
        <ul className="space-y-3">
            {dates.map(date => {
                const dateString = formatDateYYYYMMDD(date);
                const { day, date: dateNum } = formatDayAndDate(date);
                const entry = personalSchedule[dateString];
                const shift = entry?.shiftId ? shiftsMap.get(entry.shiftId) : null;
                const isNextShift = dateString === nextShiftDateString;
                const depId = staffMember.departmentId;
                const depDailyHours = depId ? dailyDepartmentHours[dateString]?.[depId] : 0;

                return (
                    <li key={dateString}>
                        <button 
                            onClick={() => setSelectedDateForDetails(dateString)}
                            className={`p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm flex items-center gap-4 transition-all duration-200 w-full text-left ${
                                isNextShift ? 'ring-2 ring-indigo-500' : 'hover:ring-2 hover:ring-indigo-400'
                            }`}
                        >
                            <div className="text-center w-12 flex-shrink-0">
                                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">{day}</p>
                                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{dateNum}</p>
                            </div>
                            <div className="w-px bg-gray-200 dark:bg-slate-700 self-stretch"></div>
                            <div className="flex-1">
                                {(() => {
                                    if (entry?.status === 'ASSIGNED' && shift) {
                                        return (
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: shift.color }}></span>
                                                    <div>
                                                        <p className="font-semibold text-gray-900 dark:text-gray-100">{shift.name}</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">{shift.startTime} - {shift.endTime}</p>
                                                    </div>
                                                </div>
                                                {depDailyHours !== undefined && (
                                                    <div className="text-right">
                                                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Dept Total</p>
                                                        <p className="text-sm font-bold text-gray-600 dark:text-gray-300">{depDailyHours.toFixed(1)}h</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <span className="w-3 h-3 rounded-full bg-gray-400 dark:bg-slate-600 flex-shrink-0"></span>
                                                <p className="font-medium text-gray-600 dark:text-gray-300 capitalize">{(entry?.status || 'UNASSIGNED').replace('_', ' ').toLowerCase()}</p>
                                            </div>
                                            {depDailyHours !== undefined && (
                                                <div className="text-right">
                                                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Dept Total</p>
                                                    <p className="text-sm font-bold text-gray-600 dark:text-gray-300">{depDailyHours.toFixed(1)}h</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            {isNextShift && (
                                <span className="text-xs font-bold text-white bg-indigo-500 px-2 py-1 rounded-full animate-pulse">
                                    NEXT
                                </span>
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );

    const renderScheduleView = () => {
        const WeekHeader = ({ weekTitle, dates, hours, depHours }: { weekTitle: string, dates: Date[], hours: number, depHours: number }) => (
            <div className="flex items-center justify-between w-full gap-4">
                <div className="min-w-0">
                    <p className="font-semibold text-lg text-gray-800 dark:text-gray-200">{weekTitle}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{formatDateRange(dates[0])}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex-shrink-0 text-center bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 rounded-lg p-2 w-20 border border-gray-200 dark:border-slate-600">
                        <p className="font-bold text-xl leading-tight">{depHours.toFixed(1)}</p>
                        <p className="text-[10px] opacity-80 leading-tight uppercase font-bold">Dept</p>
                    </div>
                    <div className="flex-shrink-0 text-center bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg p-2 w-20 shadow-inner">
                        <p className="font-bold text-xl leading-tight">{hours.toFixed(1)}</p>
                        <p className="text-[10px] opacity-80 leading-tight uppercase font-bold">Yours</p>
                    </div>
                </div>
            </div>
        );
        
        const depId = staffMember.departmentId;

        return (
            <div className="space-y-4">
                <Accordion title={<WeekHeader weekTitle="This Week" dates={thisWeekDates} hours={thisWeekHours} depHours={depId ? departmentWeeklyHours.this[depId] : 0} />}>
                    {renderScheduleList(thisWeekDates)}
                </Accordion>
                <Accordion title={<WeekHeader weekTitle="Next Week" dates={nextWeekDates} hours={nextWeekHours} depHours={depId ? departmentWeeklyHours.next[depId] : 0} />}>
                    {renderScheduleList(nextWeekDates)}
                </Accordion>
            </div>
        );
    };
    
    const renderAvailabilityView = () => (
        <div className="space-y-4">
            <Accordion title="Date-Specific Overrides">
                 <AvailabilityCalendar
                    availability={editableStaff.availability}
                    availabilityOverrides={editableStaff.availabilityOverrides || {}}
                    weekStartDay={weekStartDay}
                    onOverridesChange={handleOverridesChange}
                    onAvailabilityChange={handleDefaultAvailabilityChange}
                    variant="overrides"
                    themeStyle="staff"
                    scheduleForUser={personalSchedule}
                />
            </Accordion>
            <Accordion title="Default Weekly Schedule">
                <AvailabilityCalendar
                    availability={editableStaff.availability}
                    availabilityOverrides={editableStaff.availabilityOverrides || {}}
                    weekStartDay={weekStartDay}
                    onOverridesChange={handleOverridesChange}
                    onAvailabilityChange={handleDefaultAvailabilityChange}
                    variant="defaults"
                    themeStyle="staff"
                    scheduleForUser={personalSchedule}
                />
            </Accordion>
            {hasChanges && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mt-4 p-4 text-right">
                    <button 
                        onClick={handleSaveChanges} 
                        className="inline-flex items-center gap-2 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                    >
                        <Icons.Save className="w-4 h-4" />
                        Save Availability
                    </button>
                </div>
            )}
        </div>
    );
    
    const renderProfileView = () => {
        const departmentName = departments.find(d => d.id === editableStaff.departmentId)?.name || 'N/A';

        return (
            <div className="space-y-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg flex items-center gap-2">
                        <Icons.User className="w-5 h-5 text-gray-400" />
                        Personal & Role Information
                    </h3>
                    <div className="mt-4 space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input type="text" name="name" id="name" value={editableStaff.name} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200 p-2 bg-gray-100 dark:bg-slate-700/50 rounded-md truncate">{editableStaff.role}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200 p-2 bg-gray-100 dark:bg-slate-700/50 rounded-md truncate">{departmentName}</p>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                            <textarea name="notes" id="notes" value={editableStaff.notes} onChange={handleInputChange} rows={3} className="mt-1 block w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"></textarea>
                        </div>

                        <div className="flex items-center gap-2 py-2">
                            <input
                                type="checkbox"
                                id="isDriver"
                                name="isDriver"
                                checked={editableStaff.isDriver || false}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="isDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Icons.Bus className="w-4 h-4 text-indigo-500" />
                                I am a Driver
                            </label>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg flex items-center gap-2">
                        <Icons.Lock className="w-5 h-5 text-gray-400" />
                        Contact & Security
                    </h3>
                    <div className="mt-4 space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input type="email" name="email" id="email" value={editableStaff.email} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2" />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input type="tel" name="phone" id="phone" value={editableStaff.phone} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2" />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type={passwordVisible ? 'text' : 'password'}
                                    name="password"
                                    id="password"
                                    value={editableStaff.password || ''}
                                    onChange={handleInputChange}
                                    className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 pr-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2"
                                />
                                <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                                    {passwordVisible ? <Icons.EyeOff className="h-5 w-5"/> : <Icons.Eye className="h-5 w-5"/>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {hasChanges && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mt-4 p-4 text-right sticky bottom-20 z-10">
                        <button 
                            onClick={handleSaveChanges} 
                            className="inline-flex items-center gap-2 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                        >
                            <Icons.Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderShuttleScheduleView = () => {
        const dailyReservations = shuttleReservations.filter(r => r.date === shuttleDate);
        const sortedReservations = [...dailyReservations].sort((a, b) => a.time.localeCompare(b.time));
        
        // Group by time slot
        const timeSlotsInUse = Array.from(new Set(sortedReservations.map(r => r.time))).sort();

        const handleBookingSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            if (!bookingForm.locationId || !bookingForm.roomNumber) {
                toast.error('Please fill in all required fields');
                return;
            }

            onSaveShuttleReservation({
                ...bookingForm,
                date: shuttleDate,
                createdAt: new Date().toISOString()
            });

            toast.success('Reservation saved');
            setIsBookingShuttle(false);
            setBookingForm(prev => ({ ...prev, roomNumber: '', guests: 1 }));
        };

        if (isBookingShuttle) {
            return (
                <div className="space-y-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-gray-800 dark:text-gray-200">New Shuttle Reservation</h3>
                            <button onClick={() => setIsBookingShuttle(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleBookingSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Route</label>
                                <div className="grid grid-cols-1 gap-2">
                                    <div className="flex gap-2 mb-2">
                                        <button 
                                            type="button"
                                            onClick={() => setBookingForm(prev => ({ ...prev, direction: 'FROM_HOTEL' }))}
                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${bookingForm.direction === 'FROM_HOTEL' ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300'}`}
                                        >
                                            Hotel → Location
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setBookingForm(prev => ({ ...prev, direction: 'TO_HOTEL' }))}
                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${bookingForm.direction === 'TO_HOTEL' ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300'}`}
                                        >
                                            Location → Hotel
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {pickupLocations.map(loc => (
                                            <button
                                                key={loc.id}
                                                type="button"
                                                onClick={() => setBookingForm(prev => ({ ...prev, locationId: loc.id }))}
                                                className={`px-3 py-2 rounded-lg border text-[10px] font-bold transition-all text-left flex items-center gap-2 ${bookingForm.locationId === loc.id ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-600 text-indigo-700 dark:text-indigo-300' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300'}`}
                                            >
                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: loc.color }}></div>
                                                <span className="truncate">{loc.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Time</label>
                                    <select
                                        value={bookingForm.time}
                                        onChange={(e) => setBookingForm(prev => ({ ...prev, time: e.target.value }))}
                                        className="w-full bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-lg text-sm font-bold p-2"
                                    >
                                        {timeSlots.map(slot => (
                                            <option key={slot} value={slot}>{slot}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Guests</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={bookingForm.guests}
                                        onChange={(e) => setBookingForm(prev => ({ ...prev, guests: parseInt(e.target.value) }))}
                                        className="w-full bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-lg text-sm font-bold p-2"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Room Number</label>
                                <input
                                    type="text"
                                    placeholder="e.g., 402"
                                    value={bookingForm.roomNumber}
                                    onChange={(e) => setBookingForm(prev => ({ ...prev, roomNumber: e.target.value }))}
                                    className="w-full bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-lg text-sm font-bold p-2"
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/30"
                            >
                                Confirm Reservation
                            </button>
                        </form>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                        <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg flex items-center gap-2">
                            <Icons.Bus className="w-5 h-5 text-indigo-600" />
                            Shuttle Schedule
                        </h3>
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={shuttleDate}
                                onChange={(e) => setShuttleDate(e.target.value)}
                                className="bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-md text-sm font-bold text-indigo-600 dark:text-indigo-400 py-1 px-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                                onClick={() => setIsBookingShuttle(true)}
                                className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                                title="New Reservation"
                            >
                                <Icons.Plus className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => {
                                    if (onInitiateShuttlePrint) {
                                        onInitiateShuttlePrint(shuttleDate);
                                    } else {
                                        window.print();
                                    }
                                }}
                                className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors"
                                title="Print Schedule"
                            >
                                <Icons.Printer className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <style type="text/css" media="print">
                    {`
                        @page { size: portrait; margin: 1cm; }
                        body { -webkit-print-color-adjust: exact; color-adjust: exact; background-color: white; }
                        /* Hide everything outside of the print area */
                        nav, header, .bottom-nav { display: none !important; }
                        .print\\:hidden { display: none !important; }
                    `}
                    </style>

                    {timeSlotsInUse.length > 0 ? (
                        <div className="space-y-4">
                            {timeSlotsInUse.map(slot => {
                                const slotReservations = sortedReservations.filter(r => r.time === slot);
                                const totalGuests = slotReservations.reduce((sum, r) => sum + r.guests, 0);
                                
                                return (
                                    <div key={slot} className="border-l-4 border-indigo-500 bg-gray-50 dark:bg-slate-700/30 p-3 rounded-r-lg">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-lg font-black text-indigo-600 dark:text-indigo-400 font-mono">{slot}</span>
                                            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-bold">
                                                {totalGuests} Guests
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            {slotReservations.map(res => {
                                                const location = pickupLocations.find(l => l.id === res.locationId);
                                                const routeLabel = res.direction === 'FROM_HOTEL' 
                                                    ? `Hotel → ${location?.name || 'Unknown'}`
                                                    : `${location?.name || 'Unknown'} → Hotel`;
                                                
                                                return (
                                                    <div key={res.id} className="flex items-center justify-between text-sm py-1 border-t border-gray-200 dark:border-slate-700 first:border-t-0">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: location?.color || '#ccc' }}></div>
                                                            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{routeLabel}</span>
                                                        </div>
                                                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                                                            <span className="text-gray-600 dark:text-gray-400 font-bold">{res.guests}p</span>
                                                            <span className="bg-gray-200 dark:bg-slate-600 px-1.5 py-0.5 rounded text-[10px] font-bold text-gray-700 dark:text-gray-300">R{res.roomNumber}</span>
                                                            <button 
                                                                onClick={() => shuttleDeleteHandler.initiateDelete(res)}
                                                                className="text-red-400 hover:text-red-600 transition-colors"
                                                            >
                                                                <Icons.Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-50 dark:bg-slate-700/20 rounded-lg border-2 border-dashed border-gray-200 dark:border-slate-700">
                            <Icons.Calendar className="mx-auto h-10 w-10 text-gray-300 dark:text-slate-600" />
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">No reservations for this date.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const tabs = useMemo(() => {
        return [
            { id: 'schedule', label: 'Schedule', icon: <Icons.Calendar className="w-6 h-6"/> },
            { id: 'availability', label: 'Availability', icon: <Icons.CalendarCheck className="w-6 h-6"/> },
            { id: 'shuttle', label: 'Shuttle', icon: <Icons.Bus className="w-6 h-6"/> },
            { id: 'timeOff', label: 'Time Off', icon: <Icons.Plane className="w-6 h-6"/> },
        ];
    }, []);

    const renderTimeOffView = () => {
        const statusConfig: { [key in TimeOffRequestStatus]: { text: string; bg: string; label: string; } } = {
            PENDING_REPLACER: { text: 'text-orange-800 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-900/25', label: 'Pending Cover' },
            PENDING_ADMIN: { text: 'text-yellow-800 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/25', label: 'Pending Admin' },
            APPROVED: { text: 'text-green-800 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/25', label: 'Approved' },
            REJECTED: { text: 'text-red-800 dark:text-red-300', bg: 'bg-red-100 dark:bg-red-900/25', label: 'Rejected' },
        };
        
        const myRequests = timeOffRequests.filter(r => r.employeeId === staffMember.id).sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const replacementRequests = timeOffRequests.filter(r => r.replacementEmployeeId === staffMember.id && r.status === 'PENDING_REPLACER');

        const otherStaff = allStaff.filter(s => s.id !== staffMember.id);
        const replacement = (req: TimeOffRequest) => allStaff.find(s => s.id === req.replacementEmployeeId);
        const selectedReplacement = replacementStaffId ? allStaff.find(s => s.id === replacementStaffId) : null;
        
        const formatDateForDisplay = (dateString: string) => {
            if (!dateString) return 'Select date';
            const date = new Date(dateString);
            const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000); // Adjust for timezone
            return localDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        };

        const renderCoverRequests = () => (
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg mb-3">Cover Requests for You</h3>
                {replacementRequests.length > 0 ? (
                    <ul className="space-y-3">
                        {replacementRequests.map(req => (
                            <li key={req.id} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                                <p className="font-semibold text-gray-800 dark:text-gray-200">{req.staffName} needs cover</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{req.startDate} to {req.endDate}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 italic">"{req.reason || 'No reason provided'}"</p>
                                <div className="mt-3 flex gap-3">
                                    <button
                                        onClick={() => onReplacementDecision(req.id, true)}
                                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 sm:text-sm"
                                    >
                                        <Icons.CalendarCheck className="w-4 h-4" />
                                        Accept
                                    </button>
                                    <button
                                        onClick={() => onReplacementDecision(req.id, false)}
                                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 sm:text-sm"
                                    >
                                        <Icons.X className="w-4 h-4" />
                                        Reject
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">You have no pending cover requests.</p>
                )}
            </div>
        );


        return (
            <div className="space-y-4">
                {replacementRequests.length > 0 && renderCoverRequests()}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg mb-3">Request Time Off</h3>
                    <form onSubmit={handleTimeOffSubmit} className="space-y-4">
                         <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Request Type</label>
                            <div className="mt-2 grid grid-cols-2 gap-3">
                                <label className={`relative flex cursor-pointer rounded-lg border p-3 focus:outline-none ${timeOffType === 'DIRECT' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-300 dark:border-slate-600'}`}>
                                    <input type="radio" name="timeoff-type" value="DIRECT" className="sr-only" onChange={() => setTimeOffType('DIRECT')} checked={timeOffType === 'DIRECT'} />
                                    <div className="flex flex-1">
                                        <div className="flex flex-col">
                                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-200">Direct</span>
                                            <span className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">No replacement needed</span>
                                        </div>
                                    </div>
                                </label>
                                 <label className={`relative flex cursor-pointer rounded-lg border p-3 focus:outline-none ${timeOffType === 'REPLACED' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-300 dark:border-slate-600'}`}>
                                    <input type="radio" name="timeoff-type" value="REPLACED" className="sr-only" onChange={() => setTimeOffType('REPLACED')} checked={timeOffType === 'REPLACED'} />
                                    <div className="flex flex-1">
                                        <div className="flex flex-col">
                                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-200">Replaced</span>
                                            <span className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">Find a replacement</span>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {timeOffType === 'REPLACED' && (
                            <div>
                                <label htmlFor="replacementStaffId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Replacement</label>
                                <button
                                    type="button"
                                    id="replacementStaffId"
                                    onClick={() => setIsReplacementModalOpen(true)}
                                    className="mt-1 flex items-center justify-between w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md shadow-sm text-left py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                                >
                                    {selectedReplacement ? (
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-xs"
                                                style={{ backgroundColor: selectedReplacement.color }}
                                            >
                                                {getInitials(selectedReplacement.name)}
                                            </div>
                                            <span className="font-medium text-gray-900 dark:text-gray-200">{selectedReplacement.name}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500 dark:text-gray-400">Select a staff member...</span>
                                    )}
                                     <Icons.ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                </button>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                                <button type="button" onClick={() => setIsDateModalOpen(true)} className={`mt-1 h-10 flex items-center justify-center w-full border rounded-md shadow-sm text-sm font-medium ${timeOffStartDate ? 'text-gray-900 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'} border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600`}>
                                    {formatDateForDisplay(timeOffStartDate)}
                                </button>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                                 <button type="button" onClick={() => setIsDateModalOpen(true)} className={`mt-1 h-10 flex items-center justify-center w-full border rounded-md shadow-sm text-sm font-medium ${timeOffEndDate ? 'text-gray-900 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'} border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600`}>
                                    {formatDateForDisplay(timeOffEndDate)}
                                </button>
                            </div>
                        </div>
                         <div>
                            <label htmlFor="timeOff-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reason (optional)</label>
                            <textarea id="timeOff-reason" value={timeOffReason} onChange={(e) => setTimeOffReason(e.target.value)} rows={2} className="mt-1 block w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"></textarea>
                        </div>
                        <button type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm">
                            <Icons.Plane className="w-4 h-4" />
                            Submit Request
                        </button>
                    </form>
                </div>
                 <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg mb-3">Your Requests</h3>
                    {myRequests.length > 0 ? (
                        <ul className="space-y-3 max-h-64 overflow-y-auto">
                            {myRequests.map(req => {
                                const isCancellable = req.status === 'PENDING_ADMIN' || req.status === 'PENDING_REPLACER';
                                return (
                                <li key={req.id} className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-grow">
                                            <p className="font-semibold text-gray-800 dark:text-gray-200">{req.startDate} to {req.endDate}</p>
                                             <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-4">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${req.type === 'DIRECT' ? 'bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'}`}>{req.type}</span>
                                                {req.type === 'REPLACED' && <span>Replacement: <strong>{replacement(req)?.name || 'N/A'}</strong></span>}
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{req.reason || 'No reason provided'}</p>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig[req.status].bg} ${statusConfig[req.status].text}`}>
                                                {statusConfig[req.status].label}
                                            </span>
                                            {isCancellable && (
                                                <button
                                                    onClick={() => timeOffDeleteHandler.initiateDelete(req)}
                                                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20"
                                                    title="Delete Request"
                                                >
                                                    <Icons.Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            )})}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">You haven't made any time off requests.</p>
                    )}
                </div>

                {isReplacementModalOpen && (
                    <ReplacementSelectionModal
                        staffList={otherStaff}
                        onClose={() => setIsReplacementModalOpen(false)}
                        onSelectStaff={(staff) => {
                            setReplacementStaffId(staff.id);
                            setIsReplacementModalOpen(false);
                        }}
                    />
                )}
                 {isDateModalOpen && (
                    <DateRangeSelectionModal
                        initialStartDate={timeOffStartDate}
                        initialEndDate={timeOffEndDate}
                        onClose={() => setIsDateModalOpen(false)}
                        onConfirm={(start, end) => {
                            setTimeOffStartDate(start);
                            setTimeOffEndDate(end);
                            setIsDateModalOpen(false);
                        }}
                    />
                )}
            </div>
        )
    };

    if (portalView === 'notifications') {
        return (
            <div className="max-w-md mx-auto bg-gray-50 dark:bg-slate-900 min-h-screen animate-in fade-in duration-300">
                <div className="sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-100 dark:border-slate-800 px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => setPortalView('main')} 
                                className="p-2 -ml-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <Icons.ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-200" />
                            </button>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h1>
                        </div>
                        <div className="flex gap-2">
                            {unreadCount > 0 && (
                                <button 
                                    onClick={onMarkAllNotificationsAsRead} 
                                    className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 px-2 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                                >
                                    Mark all read
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button 
                                    onClick={onClearNotifications} 
                                    className="text-xs font-bold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-4 pb-24">
                    {notifications.length > 0 ? (
                        <div className="space-y-6">
                            {(() => {
                                const displayedNotifications = notifications.slice(0, 50);
                                const unread = displayedNotifications.filter(n => !n.read);
                                const read = displayedNotifications.filter(n => n.read);

                                return (
                                    <>
                                        {unread.length > 0 && (
                                            <div>
                                                <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">New</h3>
                                                <ul className="space-y-3">
                                                    {unread.map(n => (
                                                        <NotificationItem key={n.id} notification={n} onMarkAsRead={onMarkNotificationAsRead} />
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {read.length > 0 && (
                                            <div>
                                                <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Earlier</h3>
                                                <ul className="space-y-3">
                                                    {read.map(n => (
                                                        <NotificationItem key={n.id} notification={n} onMarkAsRead={onMarkNotificationAsRead} />
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {notifications.length > 50 && (
                                            <div className="text-center py-4">
                                                <p className="text-xs text-gray-500 italic">Showing latest 50 of {notifications.length} notifications</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                            <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-6">
                                <Icons.Bell className="w-10 h-10 text-gray-300 dark:text-slate-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">All caught up!</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                You have no new notifications at the moment. We'll let you know when something important happens.
                            </p>
                            <button 
                                onClick={() => setPortalView('main')}
                                className="mt-8 px-6 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-all shadow-sm"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }


    return (
        <div className="max-w-md mx-auto bg-gray-100 dark:bg-slate-900 min-h-screen">
            <div className="p-4 space-y-4 pb-24">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => setActiveTab('profile')}
                        className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        style={{ backgroundColor: staffMember.color }}
                        title="View Profile"
                    >
                        {getInitials(staffMember.name)}
                    </button>
                    <div className="flex-grow min-w-0">
                        <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider truncate mb-0.5">{propertyName}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Welcome back,</p>
                        <h1 className="text-xl font-bold text-gray-800 dark:text-gray-200 leading-tight truncate">{staffMember.name}</h1>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                        <button
                            onClick={() => setActiveTab('help')}
                            className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            title="Help"
                        >
                            <Icons.HelpCircle className="w-5 h-5" />
                        </button>
                        <NotificationBell unreadCount={unreadCount} onClick={() => setPortalView('notifications')} />
                        <button
                            onClick={onLogout}
                            className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            title="Logout"
                        >
                            <Icons.LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                {activeTab === 'schedule' && renderScheduleView()}
                {activeTab === 'shuttle' && renderShuttleScheduleView()}
                {activeTab === 'availability' && renderAvailabilityView()}
                {activeTab === 'profile' && renderProfileView()}
                {activeTab === 'timeOff' && renderTimeOffView()}
                {activeTab === 'help' && <HelpCenterView isMobile={true} />}
                 {timeOffDeleteHandler.itemToDelete && (
                    <ConfirmationModal
                        title="Delete Time Off Request"
                        message={`Are you sure you want to delete your request for ${timeOffDeleteHandler.itemToDelete.startDate} to ${timeOffDeleteHandler.itemToDelete.endDate}?`}
                        confirmButtonText="Delete"
                        onConfirm={timeOffDeleteHandler.confirmDelete}
                        onCancel={timeOffDeleteHandler.cancelDelete}
                        isConfirming={timeOffDeleteHandler.isDeleting}
                    />
                )}
                {shuttleDeleteHandler.itemToDelete && (
                    <ConfirmationModal
                        title="Cancel Shuttle Reservation"
                        message={`Are you sure you want to cancel the reservation for Room ${shuttleDeleteHandler.itemToDelete.roomNumber} at ${shuttleDeleteHandler.itemToDelete.time}?`}
                        confirmButtonText="Cancel Reservation"
                        onConfirm={shuttleDeleteHandler.confirmDelete}
                        onCancel={shuttleDeleteHandler.cancelDelete}
                        isConfirming={shuttleDeleteHandler.isDeleting}
                    />
                )}
                 {selectedDateForDetails && (
                    <DailyShiftDetailsModal
                        date={selectedDateForDetails}
                        currentStaffMember={staffMember}
                        allStaff={allStaff}
                        schedule={schedule}
                        shifts={shifts}
                        departments={departments}
                        onClose={() => setSelectedDateForDetails(null)}
                    />
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 h-20 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                 <div className={`max-w-md mx-auto h-full grid ${tabs.length === 5 ? 'grid-cols-5' : tabs.length === 4 ? 'grid-cols-4' : tabs.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                     {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex flex-col items-center justify-center gap-1 transition-colors duration-200 ${
                                activeTab === tab.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400'
                            }`}
                        >
                            {tab.icon}
                            <span className="text-xs font-medium">{tab.label}</span>
                        </button>
                     ))}
                </div>
            </div>
        </div>
    );
};

export default StaffPortal;